import React from 'react';
import { useTranslation } from 'react-i18next';
import { Layout, Sparkles, Crown, Check } from 'lucide-react';
import { useOutfitStore } from '@/stores/outfitStore';
import { DEFAULT_TEMPLATES } from '@/types';
import type { Template } from '@/types';

const iconMap: Record<string, React.ElementType> = {
  Layout,
  Sparkles,
  Crown,
};

export const TemplateSelector: React.FC = () => {
  const { t } = useTranslation();
  const { selectedTemplate, setSelectedTemplate } = useOutfitStore();

  const handleSelect = (template: Template) => {
    if (selectedTemplate?.id === template.id) {
      setSelectedTemplate(null);
    } else {
      setSelectedTemplate(template);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-border shadow-[0_2px_12px_rgba(194,24,91,0.05)] p-5 hover:shadow-[0_6px_20px_rgba(194,24,91,0.08)] transition-shadow">
      <h3 className="font-serif italic text-lg font-bold text-foreground mb-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
          <Layout className="w-4 h-4 text-primary" />
        </div>
        {t('template.title')}
      </h3>

      <div className="space-y-2">
        {DEFAULT_TEMPLATES.map((template) => {
          const Icon = iconMap[template.icon] || Layout;
          const isSelected = selectedTemplate?.id === template.id;

          return (
            <button
              key={template.id}
              onClick={() => handleSelect(template)}
              className={`
                w-full flex items-center justify-between p-3.5 rounded-xl border-2 transition-all duration-300
                ${
                  isSelected
                    ? 'border-primary bg-secondary/50 shadow-sm shadow-primary/10'
                    : 'border-border hover:border-accent hover:bg-secondary/30'
                }
              `}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    isSelected
                      ? 'bg-gradient-to-br from-[#E91E8C] to-primary text-white shadow-md shadow-primary/20'
                      : 'bg-secondary text-primary'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <span
                  className={`font-medium tracking-wide transition-colors ${
                    isSelected ? 'text-foreground font-semibold' : 'text-muted-foreground'
                  }`}
                >
                  {t(template.name)}
                </span>
              </div>

              {isSelected && (
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center animate-spring-in">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Category indicators */}
      {selectedTemplate && (
        <div className="mt-5 pt-4 border-t border-border animate-spring-in">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
            {t('template.select')}:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {selectedTemplate.categories.map((cat) => (
              <span
                key={cat}
                className="px-3 py-1 bg-secondary text-primary font-semibold text-xs rounded-full capitalize"
              >
                {t(`outfit.${cat}`)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
